import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, Loader2, Clock, ShieldCheck } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t, toggleLanguage, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if redirected from registration with pending approval
  const pendingApproval = location.state?.pendingApproval;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t('required'));
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('welcome') + '!');
      // If we were bumped to /login by a 401 mid-session, send the user back
      // to the page they were on so they can resume their work.
      let target = '/dashboard';
      try {
        const saved = sessionStorage.getItem('capex_post_login_redirect');
        if (saved) {
          sessionStorage.removeItem('capex_post_login_redirect');
          target = saved;
        }
      } catch (_) { /* ignore */ }
      navigate(target);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || t('error');
      // Check if it's a pending approval error
      if (error.response?.status === 403 && errorMessage.includes('attente de validation')) {
        toast.error(errorMessage, { duration: 6000 });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const args = [
    { title: 'Simuler', text: 'Chiffrez un corner et projetez la rentabilité en quelques minutes.', accent: true },
    { title: 'Convaincre', text: 'Un argumentaire chiffré, clair et exportable pour chaque projet.' },
    { title: 'Suivre', text: 'Du croquis à la livraison, pilotez chaque dossier au même endroit.' },
  ];

  const inputCls = 'w-full h-[50px] px-3 rounded-ctrl text-[15px] bg-surface border border-border text-ink focus:outline-none focus:ring-2 focus:ring-orange';
  const labelCls = 'block text-[12px] font-bold uppercase tracking-[0.08em] text-text-2';

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-bg">
      {/* Moitié gauche — panneau graphite §5.4 */}
      <div className="hidden lg:flex lg:flex-[1_1_520px] bg-graphite-900 flex-col justify-between" style={{ padding: '56px 64px' }}>
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="flex flex-col items-center" data-testid="login-brand-logo">
            <span className="font-display font-extrabold leading-none" style={{ fontSize: 150 }}>
              <span className="text-white">CAPE</span><span className="text-orange">X</span>
            </span>
            <span className="font-body font-bold uppercase text-orange mt-3" style={{ fontSize: 18, letterSpacing: '0.34em' }}>Wasabi Business</span>
          </div>
          <p className="text-ondark-2 mt-10 max-w-[520px]" style={{ fontSize: 18, lineHeight: 1.5 }} data-testid="login-brand-tagline">
            CAPEX est l'outil de simulation et de pilotage des investissements corners Wasabi, pour les commerciaux terrain, l'administration et la direction.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {args.map((a) => (
            <div key={a.title} className="pt-4" style={{ borderTop: `2px solid ${a.accent ? '#F26A1B' : '#4A4C53'}` }}>
              <div className="font-display font-bold uppercase text-white tracking-[0.02em] text-[20px] leading-none mb-2">{a.title}</div>
              <p className="text-ondark-3 text-[13px] leading-snug">{a.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Moitié droite — formulaire */}
      <div className="flex-1 lg:flex-[1_1_520px] flex items-center justify-center p-8">
        <div className="w-full max-w-[420px] space-y-6">
          {/* Logo mobile (panneau gauche masqué) */}
          <div className="lg:hidden flex flex-col items-center">
            <span className="font-display font-extrabold leading-none text-[44px]">
              <span className="text-ink">CAPE</span><span className="text-orange">X</span>
            </span>
            <span className="font-body font-bold uppercase text-orange-text mt-1 text-[9px]" style={{ letterSpacing: '0.34em' }}>Wasabi Business</span>
          </div>

          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold uppercase text-ink text-4xl leading-none">Connexion</h2>
            <button
              type="button"
              onClick={toggleLanguage}
              className="h-10 px-3 rounded-ctrl text-sm font-semibold bg-surface border border-border text-ink hover:bg-surface-2 transition-colors"
              data-testid="login-lang-toggle"
            >
              {language === 'fr' ? 'EN' : 'FR'}
            </button>
          </div>

          {pendingApproval && (
            <div className="p-4 rounded-ctrl bg-orange-tint flex items-start gap-3" data-testid="login-pending-approval">
              <Clock className="w-5 h-5 text-orange-tint-text mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-orange-tint-text font-semibold">
                  {language === 'fr' ? 'Inscription réussie !' : 'Registration successful!'}
                </p>
                <p className="text-orange-tint-text text-sm">
                  {language === 'fr'
                    ? 'En attente de validation de votre profil et de vos accès. Vous recevrez un email dès que votre compte sera activé.'
                    : 'Awaiting profile and access validation. You will receive an email once your account is activated.'}
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-card">
            <div className="space-y-1.5">
              <label htmlFor="email" className={labelCls}>E-mail professionnel</label>
              <input
                id="email"
                type="email"
                placeholder="vous@exemple.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className={labelCls}>Mot de passe</label>
                <Link to="/forgot-password" className="text-xs font-semibold text-orange-text hover:underline" data-testid="forgot-password-link">
                  Mot de passe oublié ?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputCls} pr-12`}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-3 hover:text-ink"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="w-full h-[54px] inline-flex items-center justify-center gap-2 rounded-ctrl text-base font-bold bg-orange text-orange-on transition-colors hover:brightness-105 disabled:opacity-60"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Se connecter
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-sm text-text-2">
            Pas encore de compte ?{' '}
            <Link to="/register" className="font-semibold text-orange-text hover:underline">
              Demander un compte
            </Link>
          </p>

          <div className="flex items-center gap-2.5 p-3.5 rounded-ctrl bg-surface-2" data-testid="login-security-note">
            <ShieldCheck className="w-5 h-5 text-text-2 flex-shrink-0" />
            <span className="text-sm text-text-2">Données chiffrées et hébergées en Europe</span>
          </div>
        </div>
      </div>
    </div>
  );
}
